import { DeviceStatusIcon } from '../components/DeviceStatusBadge';
import {
  HardwareDeviceCategory,
  HardwareDeviceStatus,
  HardwareStatusSummary,
} from '../types/hardwareStatus';
import {
  ADC_DEVICE_STATUS_CODES,
  ADC_DEVICE_TYPE_CODES,
} from '../constants/alarm';

interface CollapsedHardwareStatuses {
  primary: HardwareDeviceStatus[];
  remaining: HardwareDeviceStatus[];
}

interface CollapsedHardwareStatusOptions {
  includePanel?: boolean;
}

const isRemoved = (hardware: HardwareDeviceStatus): boolean => hardware.status === 'removed'
  || hardware.statusCodes.includes(ADC_DEVICE_STATUS_CODES.REMOVED);

const isFrontName = (name: string): boolean => name.toLowerCase().includes('front');

const isDoorName = (name: string): boolean => name.toLowerCase().includes('door')
  || name.toLowerCase().includes('entry');

const DOOR_DEVICE_NAME_PARTS = new Set([
  'contact',
  'deadbolt',
  'door',
  'doorlock',
  'entry',
  'front',
  'lock',
  'sensor',
  'wave',
  'zwave',
]);

const getDoorLocationKey = (name: string): string => name
  .trim()
  .toLowerCase()
  .replace(/([a-z])(\d)/g, '$1 $2') // Add a space between a letter and a following digit: "door1" -> "door 1".
  .replace(/(\d)([a-z])/g, '$1 $2') // Add a space between a digit and a following letter: "1door" -> "1 door".
  .replace(/[^a-z0-9]+/g, ' ')
  .split(' ')
  .filter((part) => part && !DOOR_DEVICE_NAME_PARTS.has(part))
  .join(' ');

const findPrimaryDoorHardware = (hardwareStatuses: HardwareDeviceStatus[]): {
  doorContact?: HardwareDeviceStatus;
  doorLock?: HardwareDeviceStatus
} => {
  const activeDoorContacts = hardwareStatuses.filter(
    (hardware) => hardware.category === 'contact_sensor'
      && isDoorName(hardware.deviceName) && !isRemoved(hardware),
  );
  const activeLocks = hardwareStatuses.filter(
    (hardware) => hardware.category === 'door_lock' && !isRemoved(hardware),
  );
  const frontLocks = activeLocks.filter((hardware) => isFrontName(hardware.deviceName));
  const frontDoorContacts = activeDoorContacts.filter((hardware) => isFrontName(hardware.deviceName));

  const lockCandidates = frontLocks.length ? frontLocks : activeLocks;
  const doorContactCandidates = frontLocks.length ? frontDoorContacts : activeDoorContacts;

  const matchingPair = lockCandidates
    .map((doorLock) => ({
      doorLock,
      matchingDoorContacts: doorContactCandidates.filter(
        (doorContact) => getDoorLocationKey(doorContact.deviceName)
          === getDoorLocationKey(doorLock.deviceName),
      ),
    }))
    .find(({ matchingDoorContacts }) => matchingDoorContacts.length === 1);

  if (matchingPair) {
    return { doorContact: matchingPair.matchingDoorContacts[0], doorLock: matchingPair.doorLock };
  }

  if (lockCandidates.length === 1 && frontDoorContacts.length === 1) {
    const [doorLock] = lockCandidates;
    const [doorContact] = frontDoorContacts;
    const lockLocationKey = getDoorLocationKey(doorLock.deviceName);
    const doorLocationKey = getDoorLocationKey(doorContact.deviceName);

    if (!lockLocationKey || !doorLocationKey) {
      return { doorContact, doorLock };
    }
  }

  if (lockCandidates.length) {
    return { doorLock: lockCandidates[0] };
  }

  return { doorContact: frontDoorContacts[0] || activeDoorContacts[0] };
};

export const getCollapsedHardwareStatuses = (
  hardwareStatuses: HardwareDeviceStatus[],
  { includePanel = true }: CollapsedHardwareStatusOptions = {},
): CollapsedHardwareStatuses => {
  const { doorContact: frontDoor, doorLock: frontDoorLock } = findPrimaryDoorHardware(hardwareStatuses);
  const panels = hardwareStatuses.filter(
    (hardware) => hardware.deviceType === ADC_DEVICE_TYPE_CODES.PARTITION,
  );
  const panel = panels.find((hardware) => !isRemoved(hardware)) || panels[0];
  const primary = [
    frontDoor,
    frontDoorLock,
    ...(includePanel ? [panel] : []),
  ].filter(
    (hardware): hardware is HardwareDeviceStatus => Boolean(hardware),
  );
  const primarySet = new Set(primary);

  return { primary, remaining: hardwareStatuses.filter((hardware) => !primarySet.has(hardware))};
};

export const getDeviceStatusIcon = (status?: HardwareStatusSummary): DeviceStatusIcon => {
  if (!status || status.status === 'unknown') {
    return 'unknown';
  }

  return 'system';
};

export const getLockStatusIcon = (status?: HardwareStatusSummary): DeviceStatusIcon => {
  if (!status || status.status === 'unknown' || status.status === 'not_available') {
    return 'unknown';
  }
  if (status.status === 'unlocked' || status.status === 'open' || status.status === 'opening') {
    return 'unlock';
  }
  if (status.status === 'locked' || status.status === 'closed' || status.status === 'closing') {
    return 'lock';
  }

  return 'lock';
};

const HARDWARE_STATUS_ICONS: {
  [category in Exclude<HardwareDeviceCategory, 'door_lock' | 'other'>]: DeviceStatusIcon;
} = {
  system: 'system',
  garage_door: 'garage',
  thermostat: 'thermostat',
  contact_sensor: 'contact',
  motion_sensor: 'motion',
  smoke_sensor: 'smoke',
  environment_sensor: 'environment',
  water_sensor: 'water',
  camera: 'camera',
  light: 'light',
  glass_break_sensor: 'glass_break',
  panic_button: 'panic',
  control: 'control',
  siren: 'siren',
  doorbell: 'doorbell',
  repeater: 'repeater',
  gateway: 'gateway',
  power: 'power',
  location: 'location',
  medical: 'medical',
  audio: 'audio',
  safe: 'safe',
  access: 'access',
  module: 'module',
};

const getOtherHardwareStatusIcon = (
  hardware: HardwareDeviceStatus,
): DeviceStatusIcon => (
  hardware.status === 'unknown' || hardware.status.startsWith('unknown_') ? 'unknown' : 'device'
);

export const getHardwareStatusIcon = (
  hardware: HardwareDeviceStatus,
): DeviceStatusIcon => {
  const { category } = hardware;

  if (category === 'door_lock') {
    return getLockStatusIcon(hardware);
  }

  if (category === 'other') {
    return getOtherHardwareStatusIcon(hardware);
  }

  return HARDWARE_STATUS_ICONS[category] || getOtherHardwareStatusIcon(hardware);
};
